import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr/CheckCircle";
import { MinusCircleIcon } from "@phosphor-icons/react/dist/ssr/MinusCircle";
import { WarningIcon } from "@phosphor-icons/react/dist/ssr/Warning";

import type { CampaignRequestAgent, CampaignRequestDetail } from "@/hooks/api/use-campaign-requests";

/**
 * The small pieces the four preparation tabs share.
 *
 * Nothing here is a variant of anything in `../../components` — those are the cross-screen
 * components and this file deliberately does not shadow one. What lives here is the handful of
 * shapes that only preparation needs: the one-line form that unbuilt work is allowed to take, an
 * empty state and a checklist mark. What an agent is *called* is `../../view/agent-naming`: the
 * client screen and this one must answer that the same way or a renamed agent has two names.
 */

/** One line of the readiness checklist. `warn` never blocks — it is a thing to have looked at. */
export type CheckTone = "ok" | "warn" | "none";

export interface CheckItem {
	key: string;
	tone: CheckTone;
	label: React.ReactNode;
}

/**
 * Work this product does not do yet — one muted line, and never more.
 *
 * This used to be a dashed panel of three paragraphs explaining which phase the feature was in,
 * what the API did not store and what the screen would look like when it did. Three of them were
 * on this screen at once, rendered at the same weight as the request's own data, and they were the
 * single loudest thing on it. A person preparing a campaign does not need the architecture; they
 * need to know the box is empty and stop looking. So: one line, `text.disabled`, sitting exactly
 * where the missing thing would have been.
 */
export function MissingLine({ children }: { children: React.ReactNode }): React.JSX.Element {
	return (
		<Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
			{children}
		</Typography>
	);
}

/**
 * Nothing here yet, drawn rather than typed.
 *
 * Half of this screen is legitimately empty most of the time — no lead list, no agents, no events
 * — and "No agents on this request." as a bare grey sentence reads like a rendering failure. A
 * framed, centred panel reads like a state the product knows it is in. One sentence only; the
 * paragraph that used to follow each of these is gone.
 */
export function EmptyPanel({
	icon,
	title,
	children,
	action,
}: {
	icon: React.ReactNode;
	title: string;
	children?: React.ReactNode;
	action?: React.ReactNode;
}): React.JSX.Element {
	return (
		<Stack
			spacing={1}
			sx={{
				alignItems: "center",
				border: "1px dashed var(--mui-palette-divider)",
				borderRadius: 1,
				px: 3,
				py: 4,
				textAlign: "center",
			}}
		>
			<Box sx={{ color: "text.disabled", display: "flex" }}>{icon}</Box>
			<Typography variant="subtitle2">{title}</Typography>
			{children ? (
				<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
					{children}
				</Typography>
			) : null}
			{action ? <Box sx={{ pt: 1 }}>{action}</Box> : null}
		</Stack>
	);
}

/** A label and its value, aligned down a column so a stack of them reads as a record. */
export function Fact({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
	return (
		<Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
			<Typography variant="caption" color="text.secondary" sx={{ minWidth: 150 }}>
				{label}
			</Typography>
			<Typography variant="body2">{value}</Typography>
		</Stack>
	);
}

export function CheckMark({ tone }: { tone: CheckTone }): React.JSX.Element {
	if (tone === "ok") return <CheckCircleIcon weight="fill" color="var(--mui-palette-success-main)" />;
	if (tone === "warn") return <WarningIcon weight="fill" color="var(--mui-palette-warning-main)" />;
	return <MinusCircleIcon color="var(--mui-palette-text-secondary)" />;
}

/** An agent answers in English when its BCP 47 tag starts `en`. Everything else needs translation. */
export function isEnglish(agent: CampaignRequestAgent): boolean {
	return agent.language.toLowerCase().startsWith("en");
}

/**
 * Where the agent came from, as data and nothing else.
 *
 * The Source column used to hold a filename on one row and, on the next, the wizard's coaching
 * line — "Upload your call flow and we'll build it." — which is an instruction to somebody on a
 * different screen at a different time, printed as though it were a property of the agent. Only
 * the provenance survives here. A translated or redeployed agent knows it came from another agent
 * but carries only a `sourceAgentId`, and half a uuid identifies nothing, so the fact is stated
 * and the id is not.
 */
export function agentSource(agent: CampaignRequestAgent, documents: CampaignRequestDetail["documents"]): string | null {
	if (agent.callFlowDocumentId) {
		const document = documents.find((candidate) => candidate.id === agent.callFlowDocumentId);
		if (document) return document.originalFilename;
	}

	if (agent.sourceAgentId) return "From an agent you already run";
	if (agent.kind === "translated_agent") return "From the English flow";

	return null;
}
